import { createContext, useCallback, useContext } from "react";

export type PreservedData<TData> = null | {
	id: string;
	data: TData;
};

interface ExplorationDrawerPreserveStateContextValue<TData> {
	preservedState: PreservedData<TData>;

	setPreservedState: (data: PreservedData<TData>) => void;
}

const ExplorationDrawerPreserveStateContext = createContext<unknown>({
	preservedState: null,
	setPreservedState: () => {},
});

export const ExplorationDrawerPreserveStateContextProvider = ExplorationDrawerPreserveStateContext.Provider;

export function usePreserveExplorationDrawerState<T>(id: undefined | string): {
	clear: VoidFunction;
	preserve: (state: T) => void;
	state: undefined | T;
} {
	const { preservedState, setPreservedState } = useContext(
		ExplorationDrawerPreserveStateContext,
	) as ExplorationDrawerPreserveStateContextValue<T>;

	const clear = useCallback(() => {
		setPreservedState(null);
	}, [setPreservedState]);

	const preserve = useCallback(
		(state: T) => {
			if (id != null) {
				setPreservedState({ id, data: state });
			}
		},
		[setPreservedState, id],
	);

	return {
		clear,
		preserve,
		state: preservedState?.id === id ? preservedState?.data : undefined,
	};
}
